import * as React from 'react';
import DashedLine from 'react-native-dashed-line';
import Util from 'src/Util';
import Styles from '../../Styles';
import AVFormattedCurrency from '../AVFormattedCurrency';
import type {TransactionDetail} from 'src/types/TransactionDetail';
import {PixelRatio, StyleSheet, View, ScrollView} from 'react-native';
import AVText from '../AVText';
import AccountStore from 'src/stores/AccountStore';
import {WithLocalizedProps} from 'src/types/PropTypes';
import Localized from 'src/constants/AppStrings';

import {getDescriber as getSnackDescriber} from 'src/components/screens/sendSnack/descriptor/sendasnack/DescriptorType';
import {getDescriber} from 'src/components/elements/account/descriptor/DescriptorType';

type PromoCreditDetailProps = WithLocalizedProps & {
  detail: TransactionDetail;
  previousRoute?: string;
};

const PromoCreditDetail = (props: PromoCreditDetailProps) => {
  const formattedDate = Util.formatDate(props.detail?.DateString, 'LLL');
  return (
    <>
      <View
        style={getDescriber().getBalanceItemStyleDescriptor().promoContainer}
      >
        <ScrollView style={styles.historyContainer}>
          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
            accessible={true}
            accessibilityLabel={`${props.strings.Labels.location} ${props.detail?.Location}`}
            aria-label={`${props.strings.Labels.location} ${props.detail?.Location}`}
            style={[
              styles.location,
              getSnackDescriber()?.snackDetailsBtnFontSTyle(),
            ]}
          >
            {props.detail?.Location}
          </AVText>
          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
            accessible={true}
            accessibilityLabel={`${props.strings.Labels.location} ${props.detail?.LocationAddress}`}
            aria-label={`${props.strings.Labels.location} ${props.detail?.LocationAddress}`}
            style={[
              styles.address,
              getSnackDescriber()?.snackDetailsBtnFontSTyle(),
            ]}
          >
            {`${props.detail?.LocationAddress} ${props.detail?.LocationCity} ${props.detail?.LocationZip},  ${props.detail?.LocationState} `}
          </AVText>

          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
            accessible={true}
            accessibilityLabel={`${props.strings.Labels.date} ${formattedDate}`}
            aria-label={`${props.strings.Labels.date} ${formattedDate}`}
            style={[
              styles.promoCreditdetailDate,
              getSnackDescriber()?.snackDetailsBtnFontSTyle(),
            ]}
          >
            {formattedDate}
          </AVText>

          <AVText
            maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
            accessible={true}
            accessibilityLabel={`${props.detail?.Id}`}
            aria-label={`${props.detail?.Id}`}
            style={[
              styles.promoCreditdetailTrans,
              getSnackDescriber()?.snackDetailsBtnFontSTyle(),
            ]}
          >
            {props.detail?.Id}
          </AVText>

          <DashedLine
            dashThickness={PixelRatio.roundToNearestPixel(2)}
            dashGap={PixelRatio.roundToNearestPixel(2)}
            style={styles.dashStyle}
            dashLength={PixelRatio.roundToNearestPixel(2)}
            dashColor={getDescriber().payDashedLine()}
          />
          <View style={[styles.row]}>
            <View style={[styles.leftView]}>
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
                accessible={true}
                accessibilityLabel={props.detail?.Reason}
                aria-label={props.detail?.Reason}
                style={[
                  styles.promoDesc,
                  getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                ]}
              >
                {Localized.Labels.formatString(
                  Localized.Labels.promo_credit_offer_name,
                  props.detail?.Reason,
                )}
              </AVText>
            </View>
            {getDescriber().promoCreditAmount(styles, props)}
          </View>

          <DashedLine
            dashGap={PixelRatio.roundToNearestPixel(2)}
            dashLength={PixelRatio.roundToNearestPixel(2)}
            dashThickness={PixelRatio.roundToNearestPixel(2)}
            dashColor={getDescriber().payDashedLine()}
            style={styles.dashStyle}
          />
          <View accessible={true} style={styles.totalMainView}>
            <View style={[Styles.Style.labelColumn, styles.totalLeftView]}>
              <AVText
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
                accessibilityLabel={props.strings.Labels.total}
                aria-label={props.strings.Labels.total}
                style={[
                  getDescriber().getBalanceItemStyleDescriptor().totalText,
                  getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                  styles.totalViewMargin,
                ]}
              >
                {getDescriber().promoCreditTotalText()}
              </AVText>
            </View>
            <View style={[Styles.Style.amountColumn, styles.totalRightView]}>
              <AVFormattedCurrency
                aria-label={`$${props.detail.Amount}`}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
                style={[
                  getDescriber().getBalanceItemStyleDescriptor().totalText,
                  getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                  styles.totalViewMargin,
                ]}
                value={props.detail.Amount}
                currency={AccountStore.getCurrency()}
                accessibilityLabel={`$${props.detail.Amount}`}
              />
            </View>
          </View>
        </ScrollView>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  dashStyle: {
    borderRadius: 100,
    height: PixelRatio.roundToNearestPixel(2),
    marginBottom: Styles.Spacing.m2,
    marginTop: Styles.Spacing.m2,
    overflow: 'hidden',
    width: '100%',
  },
  date: {
    fontSize: Styles.Fonts.f0,
    marginBottom: Styles.Spacing.m3,
  },
  disabledRefundButton: {
    opacity: 0.5,
  },
  historyContainer: {
    flex: 1,
    marginBottom: Styles.Spacing.m5,
    padding: Styles.Spacing.m3,
    paddingVertical: Styles.Spacing.m3,
  },
  largeLabel: {
    fontSize: Styles.Fonts.f1,
  },
  location: {
    fontSize: Styles.Fonts.f7,
    fontWeight: 'bold',
    color: Styles.black,
    paddingBottom: 4,
  },
  address: {
    fontSize: Styles.Fonts.f8,
    fontWeight: '400',
    color: Styles.black,
    paddingBottom: Styles.Fonts.f8,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flex: 1,
  },
  leftView: {
    flex: 0.7,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  rightView: {
    flex: 0.3,
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
  },
  rightViewCpCk: {
    flex: 0.3,
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  promoDesc: {
    color: Styles.tabBarTextColor,
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
  },
  totalLeftView: {
    flex: 0.7,
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  totalRightView: {
    flex: 0.3,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  totalViewMargin: {
    marginBottom: 0,
    marginTop: 0,
  },
  totalMainView: {flex: 1, flexDirection: 'row'},
  promoCreditdetailTrans: {
    color: Styles.Colors.PayNew.darkHuesBase04,
    fontSize: Styles.Fonts.f0 + 2,
    fontWeight: '400',
    marginBottom: 10,
  },
  promoCreditdetailDate: {
    color: Styles.tabBarTextColor,
    fontSize: Styles.Spacing.m3,
    fontWeight: '400',
    paddingBottom: 2,
  },
  totalText: {
    color: Styles.tabBarTextColor,
    fontSize: Styles.Spacing.m3 + Styles.Spacing.m2,
    fontWeight: '700',
  },
});
export default PromoCreditDetail;
